import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { DeleteDialog, PRICING_KEY, PricingNav, QUOTE_BLOCKS_RESOURCE } from './PricingLayout';
import { milliToDays } from './units';
import { leaveGuarded, useUnsavedGuard, validForm } from '../ui/unsaved';

type BlockSummary = { id: string; name: string; lineCount: number; version: number };
type BlockLine = { sort: number; sectionName: string; type: 'item' | 'package' | 'service' | 'custom'; name: string; quantity: number; daysMilli: number | null; unitPriceGrosze?: number | null; description: string };
type Block = { id: string; name: string; lines?: BlockLine[]; version: number };

const KIND: Record<BlockLine['type'], string> = { item: 'sprzęt', package: 'pakiet', service: 'usługa', custom: 'pozycja własna' };
const QUOTE_BLOCKS_KEY = [...PRICING_KEY, 'quote-blocks'] as const;

/** The block's lines (prices: kept ones, otherwise "z cennika"; absent for a role that may not see prices). */
function BlockLines({ id }: { id: string }) {
  const q = useQuery({ queryKey: [...QUOTE_BLOCKS_KEY, id], queryFn: () => api<Block>(`/api/quotes/blocks/${id}`) });
  if (q.isLoading) return <p className="muted">Wczytywanie…</p>;
  if (q.error) return <p className="alert">{q.error instanceof ApiError ? q.error.message : 'Nie udało się wczytać bloku'}</p>;
  const lines = q.data?.lines ?? [];
  return (
    <ul className="q-block-lines">
      {lines.map((l) => (
        <li key={l.sort}>
          {l.quantity.toLocaleString('pl-PL')} × <strong>{l.name}</strong> <span className="muted">({KIND[l.type]}{l.daysMilli !== null ? `, ${milliToDays(l.daysMilli)} dni` : ''})</span>
          {l.unitPriceGrosze !== undefined && <span className="muted"> · {l.unitPriceGrosze === null ? 'cena z cennika' : formatPln(l.unitPriceGrosze)}</span>}
        </li>
      ))}
    </ul>
  );
}

function RenameForm({ block, onClose }: { block: BlockSummary; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(block.name);
  const save = useMutation({
    mutationFn: () => api(`/api/quotes/blocks/${block.id}`, { method: 'PATCH', body: { name, version: block.version } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRICING_KEY });
      onClose();
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: name !== block.name,
    save: async (el) => {
      if (!validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <form
      ref={guardRef}
      className="panel form"
      aria-label={`Zmiana nazwy: ${block.name}`}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {error?.status === 409 && (
        <ConflictNotice
          onReload={() => {
            void queryClient.invalidateQueries({ queryKey: PRICING_KEY });
            onClose();
          }}
        />
      )}
      {error && error.status !== 409 && <p className="alert" role="alert">{error.message}</p>}
      <label className="field">
        <span>Nazwa</span>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
      </label>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          Zapisz
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/**
 * "Cenniki → Bloki kosztorysów" (F26): sections of quotes saved as blocks ("Zapisz sekcję jako blok" in the quote's "…"),
 * inserted with "Wstaw blok". Here they are looked at, renamed and deleted.
 */
export function QuoteBlocksPage() {
  const { me } = useMe();
  const [openId, setOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<BlockSummary | null>(null);
  const list = useQuery({ queryKey: QUOTE_BLOCKS_KEY, queryFn: () => api<{ blocks: BlockSummary[] }>('/api/quotes/blocks') });
  if (!me) return null;
  const blocks = list.data?.blocks ?? [];
  return (
    <section className="page" data-feature-id="quotes.blocks">
      <div className="page-head">
        <h2>Cenniki</h2>
      </div>
      <PricingNav me={me} />
      <p className="muted">Blok kosztorysu to zapisana sekcja kosztorysu (np. „Realizacja” z usługami). Tworzysz go w kosztorysie: menu „…” → „Zapisz sekcję jako blok”, a wstawiasz przez „Wstaw blok…” w trybiku ⚙ przy polu „Dodaj…”.</p>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać bloków'}</p>}
      {list.data && blocks.length === 0 && <p className="muted">Brak bloków kosztorysów.</p>}
      {blocks.length > 0 && (
        <div className="table-wrap">
          <table className="table pricing-table" aria-label="Bloki kosztorysów">
            <thead>
              <tr>
                <th scope="col">Nazwa</th>
                <th scope="col" className="num">
                  Pozycje
                </th>
                <th scope="col" className="tpl-actions-col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => {
                const more = [
                  ...(can(me, QUOTE_BLOCKS_RESOURCE, 'edit') ? [{ label: 'Zmień nazwę', onSelect: () => leaveGuarded(() => setRenamingId(b.id)) }] : []),
                  ...(can(me, QUOTE_BLOCKS_RESOURCE, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(b) }] : []),
                ];
                return (
                  <Fragment key={b.id}>
                    <tr>
                      <td>
                        <strong>{b.name}</strong>
                      </td>
                      <td className="num">{b.lineCount}</td>
                      <td className="tpl-actions">
                        <button className="btn small" type="button" aria-expanded={openId === b.id} onClick={() => setOpenId(openId === b.id ? null : b.id)}>
                          {openId === b.id ? 'Zwiń' : 'Pokaż'}
                        </button>
                        <RowMenu label={`Więcej działań: ${b.name}`} actions={more} />
                      </td>
                    </tr>
                    {openId === b.id && (
                      <tr>
                        <td colSpan={3}>
                          <BlockLines id={b.id} />
                        </td>
                      </tr>
                    )}
                    {renamingId === b.id && (
                      <tr className="row-editor">
                        <td colSpan={3}>
                          <RenameForm block={b} onClose={() => setRenamingId(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {deleting && <DeleteDialog title="Usunąć blok kosztorysu?" name={deleting.name} url={`/api/quotes/blocks/${deleting.id}?version=${deleting.version}`} onClose={() => setDeleting(null)} />}
    </section>
  );
}
